// Live queue figures in the header (backlog 128, vault #367, Storm
// 2026-10-21). Pure helpers so scripts/verify-queue-stats.mjs can prove the
// display rules without a browser. The server (knktr-queue-stats) decides
// WHICH clients an agent sees; the console only shows what comes back.

export const POLL_MS = 10000;
export const STALE_MS = 45000;   // older than this -> dashes, never old numbers

// 0 -> "0:00", 75 -> "1:15", 3725 -> "1:02:05"
export function formatWait(sec) {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  const pad = n => String(n).padStart(2, '0');
  return h ? `${h}:${pad(m)}:${pad(r)}` : `${m}:${pad(r)}`;
}

// Smooth wait timer (Storm 2026-10-22: it jumped 3 -> 23 -> 43 s between
// reads). A wait read from Connect keeps counting up locally, one second at a
// time, from Connect's snapshot time; each new read re-syncs it. A snapshot
// time in the future or more than 30 s older than our receipt (clock skew)
// is ignored and our receipt time is used instead.
export function liveWaitSec(sec, data, now = Date.now()) {
  const base = Number(sec) || 0;
  if (base <= 0) return 0;
  const received = Number(data?.receivedAt) || now;
  const snap = Date.parse(data?.asOf ?? '');
  const from = Number.isFinite(snap) && snap <= received && received - snap <= 30000 ? snap : received;
  return base + Math.max(0, Math.floor((now - from) / 1000));
}

// What the two live header boxes show. null figures -> the dash.
export function headerFigures(data, now = Date.now()) {
  const t = data?.totals;
  const fresh = data?.receivedAt && now - data.receivedAt <= STALE_MS;
  if (!t || !fresh) return { inQueue: null, longestWait: null, longestWaitClient: null };
  return {
    inQueue: String(t.waiting ?? 0),
    longestWait: formatWait(liveWaitSec(t.longestWaitSec, data, now)),
    longestWaitClient: t.longestWaitSec > 0 ? (t.longestWaitClient || null) : null,
  };
}

// The click-open breakdown: one line per approved client, then the people
// line (each person once across all their clients).
export function breakdownRows(data, now = Date.now()) {
  const rows = (data?.clients || []).map(c => ({
    key: c.tenantId,
    name: c.name,
    waiting: c.waiting ?? 0,
    longestWait: formatWait(liveWaitSec(c.longestWaitSec, data, now)),
    agents: `${c.agentsAvailable ?? 0} free of ${c.agentsOnline ?? 0} online`,
  }));
  const t = data?.totals;
  const people = t ? `${t.agentsAvailable ?? 0} free of ${t.agentsOnline ?? 0} online (each person counted once)` : null;
  return { rows, people };
}

export async function fetchQueueStats({ url, headers, fetchImpl = fetch, now = () => Date.now() }) {
  if (!url) return null;
  try {
    const res = await fetchImpl(url, { method: 'GET', headers });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.ok === false || !data.totals) return null;
    return { ...data, receivedAt: now() };
  } catch {
    return null;
  }
}
